import React, { useEffect, useMemo } from "react";
import ScreenCard from "../ScreenCard";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useQuery } from "@apollo/client/react";
import { useI18n } from "../../hooks/i18n-hooks";

import { FIND_MY_SALARIES, GET_SALARY } from "../../graphql/salaries";
import apolloClient from "../../configs/graphql.config";
import { useSnackbar } from "../../hooks/snackbar";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import EmptyScreen from "../../components/EmptyScreen";
import NoSalariesImage from "../../assets/images/no-salaries-image.svg";
import ListGroup from "../../components/ListGroup";
import { capitalizeFirstLetter, formatCurrency } from "../../utils";
import { useDateFormatter } from "../../hooks/date";
import { useNavigationHeader } from "../../hooks/use-navigation-header";

type SalaryPayslipsScreenProps = StackScreenProps<RootStackParamList, "SalaryPayslips">;

const SalaryPayslipsScreen = ({ route, navigation }: SalaryPayslipsScreenProps) => {
    const i18n = useI18n();
    const { showSnackbar } = useSnackbar();
    const intlFormat = useDateFormatter();

    const { salaryId } = useMemo(() => route.params, [route.params]);
    const { data, error, loading } = useQuery(GET_SALARY, {
        variables: { salaryId },
        skip: !salaryId,
    });

    const salary = data?.getSalary;
    const payslips = salary?.payslips;
    useNavigationHeader(
        salary
            ? capitalizeFirstLetter(
                  intlFormat(new Date(salary.year, salary.month - 1, 1), {
                      year: "numeric",
                      month: "long",
                  }),
              )
            : " ",
    );

    useEffect(() => {
        // check if salary still exists,
        // backend return null if salary does not exist or is not accessible by the current user
        if (data && data.getSalary === null) {
            void apolloClient.refetchQueries({
                include: [FIND_MY_SALARIES],
            });
            // notify user
            showSnackbar({
                text: i18n.t(`salary.salary_not_found`),
                mode: "warning",
            });
            if (navigation.canGoBack()) {
                navigation.goBack();
            } else {
                navigation.popTo("Home", { screen: "SalariesScreen" });
            }
        }
    }, [data, i18n, navigation, showSnackbar]);

    const items = useMemo(() => {
        return (payslips || []).map((payslip) => {
            return {
                value: payslip.id,
                title: payslip.missionInfo?.clientInfo?.name,
                description: formatCurrency({ amountInCents: payslip.netAmount }),
                avatarPath: payslip.missionInfo?.clientInfo?.logoUrl || undefined,
                tags: [
                    payslip.paidDate
                        ? capitalizeFirstLetter(
                              intlFormat(new Date(payslip.paidDate), {
                                  day: "numeric",
                                  year: "numeric",
                                  month: "long",
                              }),
                          )
                        : "",
                ],
                onPress: () => {
                    if (payslip.asset != null) {
                        navigation.navigate("PayslipDetails", {
                            payslipId: payslip.id,
                        });
                    }
                },
            };
        });
    }, [payslips, intlFormat, navigation]);

    return (
        <LoadingBoundary loading={loading && !data} error={error}>
            <ScreenCard>
                {items.length === 0 ? (
                    <EmptyScreen
                        image={NoSalariesImage}
                        text={i18n.t("screens.user.salaries.no_payslips")}
                    />
                ) : (
                    <ListGroup items={items} />
                )}
            </ScreenCard>
        </LoadingBoundary>
    );
};

export default SalaryPayslipsScreen;
